import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    FormField,
    Button,
    Icon,
    Notification,
    FormFieldMultiline
} from "@weirgroup/weir-dls";
import { BucketDataType, EditPartType, NonEscoPartParamsType, ReplacementPartType } from "../../../types/bucketTypes";
import assetsApi from '../../../api/assets.api';
import { NON_ESCO } from "../../../constants/bucket.contants";
import { LoadingSpinner } from "../../loading-spinner";
import { useDarkMode } from "../../../hooks/use-darkmode";

interface Props {
    setShowAddNonEscoPart: Dispatch<SetStateAction<boolean>>
    setActiveTabIndex: Dispatch<SetStateAction<number>>
    selectedAsset: BucketDataType;
    setSelectedAsset: Dispatch<SetStateAction<any>>;
    editPart?: ReplacementPartType;
    fetchBucketList?: (arg?: boolean) => Promise<void>;
}

export const AddNonEsco = (props: Props) => {
    const {
        setShowAddNonEscoPart,
        setActiveTabIndex,
        selectedAsset,
        setSelectedAsset,
        editPart,
        fetchBucketList
    } = props;

    const { t } = useTranslation();
    const { darkMode } = useDarkMode()
    const [pmid, setPMID] = useState<string>(editPart ? editPart.pmid : "")
    const [description, setDescription] = useState<string>(editPart ? editPart.description : "")
    const [quantity, setQuantity] = useState<number>(editPart ? editPart.quantity : 0)
    const [partNumber, setPartNumber] = useState<string>(editPart ? editPart.part_number : "")
    const [notes, setNotes] = useState<string>(editPart ? editPart.notes : "")
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [showToast, setShowToast] = useState<boolean>(false)
    const [toastData, setToastData] = useState<any>()
    const [showError, setShowError] = useState<boolean>(false)

    useEffect(() => {
        const data = [pmid, description, quantity, partNumber]
        setShowError(isNotComplete(data));
    }, [pmid, description, quantity, partNumber])

    useEffect(() => {
        if (showToast) {
            setTimeout(() => setShowToast(false), 3000)
        }
    }, [showToast])

    const goBack = () => {
        setShowAddNonEscoPart(false);
        setIsLoading(false);
        setActiveTabIndex(1);
    }

    const isNotComplete = (data: any) => {
        const values: string[] = Object.values(data)
        return values.indexOf('') >= 0 || data.quantity == 0;
    }

    const showErrorMessage = (message?: string) => {
        setToastData({
            message,
            variant: "critical"
        })
        setShowToast(true)
        setIsLoading(false)
    }

    const addPart = () => {
        setIsLoading(true);
        if (editPart) {
            const editData: EditPartType = {
                part_id: editPart?.part_id,
                pmid: pmid !== "" ? pmid : editPart?.pmid,
                description: description !== "" ? description : editPart?.description,
                quantity: quantity ?? editPart?.quantity,
                part_number: partNumber !== "" ? partNumber : editPart?.part_number,
                notes: notes,
                type: NON_ESCO
            }

            if (!showError) {
                assetsApi.editReplacementPart(editData).then(() => {
                    if (fetchBucketList) {
                        fetchBucketList(true);
                    }
                    goBack();
                }).catch(() => {
                    showErrorMessage(t("notificationTitleError"))
                    goBack();
                })
            } else {
                showErrorMessage(t("addPartValidationMessage"));
            }
        } else {
            const addData: NonEscoPartParamsType = {
                asset_id: selectedAsset.asset_id,
                pmid: pmid,
                description,
                quantity,
                part_number: partNumber,
                notes,
                type: NON_ESCO
            }
            const removedNotes: any = { ...addData }
            delete removedNotes.notes;

            if (!isNotComplete(removedNotes)) {
                assetsApi.addNonEscoPart(addData).then((result: any) => {
                    setSelectedAsset(result.data.data);
                    goBack();
                }).catch(() => {
                    showErrorMessage(t("notificationTitleError"))
                    goBack();
                });
            } else {
                showErrorMessage(t("addPartValidationMessage"));
            }
        }
    }

    return (
        <div className="add-non-esco">
            {isLoading ? <LoadingSpinner darkMode={darkMode} /> : <></>}
            <p className="title" onClick={() => goBack()} role="button">
                <span className="serial_number">{selectedAsset.serial_number}</span><Icon name={"Right"} /><span>{editPart ? t("editPart") : t("addNew")}</span></p>
            <p className="subtitle">{editPart ? t("editNewReplacementPart") : t("addNewReplacementPart")}</p>
            <div className="container">
                <div>
                    <div className="input-group1">
                        <FormField
                            name="InputBox"
                            required={true}
                            label={t("pmid")}
                            size={"sm"}
                            width={"100%"}
                            placeholder={editPart ? editPart.pmid : t("enterNumber")}
                            onChange={(e: any) => setPMID(e.target.value)}
                            defaultValue={editPart ? editPart.pmid : ""}
                        />
                        <div className="FormFieldPadding">
                            <FormField
                                name="InputBox"
                                required={true}
                                label={t("quantity")}
                                type={"number"}
                                min="1"
                                size={"sm"}
                                width={"100%"}
                                placeholder={editPart ? editPart.quantity : t("enterQuantity")}
                                onChange={(e: any) => setQuantity(e.target.value)}
                                onKeyDown={(e: any) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                                defaultValue={editPart ? editPart.quantity : ""}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <div className="input-group2">
                        <FormField
                            name="InputBox"
                            required={true}
                            label={t("descriptionTitle")}
                            size={"sm"}
                            width={"100%"}
                            placeholder={editPart ? editPart.description : t("enterTitle")}
                            onChange={(e: any) => setDescription(e.target.value)}
                            defaultValue={editPart ? editPart.description : ""}
                        />
                        <div className="FormFieldPadding">
                            <FormField
                                name="InputBox"
                                required={true}
                                label={t("partNumber")}
                                size={"sm"}
                                width={"100%"}
                                placeholder={editPart ? editPart.part_number : t("enterNumber")}
                                onChange={(e: any) => setPartNumber(e.target.value)}
                                defaultValue={editPart ? editPart.part_number : ""}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="multiline-textfield">
                <FormFieldMultiline
                    errorMessage=""
                    label="Notes"
                    modalType="large"
                    onChange={(e: any)=>setNotes(e)}
                    optionalText="Optional"
                    outerContainerPadding="30px"
                    placeholder={editPart ? editPart.notes : ""}
                    variant="lg"
                />
            </div>
            <div className="btn-container">
                <Button
                    label={t("cancel")}
                    size={"sm"}
                    variant={"secondary"}
                    onClick={goBack}
                />
                <Button
                    label={editPart ? t("save") : t("addPart")}
                    size={"sm"}
                    variant={"primary"}
                    onClick={addPart}
                />
            </div>
            {showToast ? <div className="toast-container">
                <Notification 
                    title={toastData.variant == "success" ? "Success" : "Fail"}
                    textContent={toastData.message}
                    variant={toastData.variant}
                    onClose={()=>{setShowToast(false)}}
                />
            </div> : <></>}
        </div>

    )
}